import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { jsPDF } from 'jspdf';

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = (template) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const lineHeight = 7;
    let y = margin;

    // Title
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(template.title, margin, y);
    y += lineHeight * 1.5;

    // Content
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    const lines = doc.splitTextToSize(template.content, pageWidth - 2 * margin);
    
    lines.forEach(line => {
      if (y + lineHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    });

    doc.save(`${template.title.replace(/\s+/g, '_')}.pdf`);
  };

  const downloadDOCX = (template) => {
    // Create a simple Word-like document
    const content = `${template.title}\n\n${template.content}`;
    const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.title.replace(/\s+/g, '_')}.doc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Templates & Resources
          </h1>
          <p className="text-lg text-slate-600">
            Download editable templates for petitions, memoranda, testimonies, and event planning
          </p>
        </div>

        {templates.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 text-lg">No templates available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {templates.map((template, index) => (
              <div
                key={template.id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                data-testid={`template-card-${index}`}
              >
                <div className="p-6">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="h-6 w-6 text-primary-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                        {template.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-3">
                        {template.description}
                      </p>
                      <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
                        {template.category}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <Button
                      onClick={() => downloadPDF(template)}
                      className="flex-1 bg-primary-700 hover:bg-primary-900 text-white rounded-lg"
                      data-testid="download-pdf-button"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                    <Button
                      onClick={() => downloadDOCX(template)}
                      variant="outline"
                      className="flex-1 rounded-lg"
                      data-testid="download-docx-button"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Word
                    </Button>
                  </div>
                </div>

                <div className="bg-surface p-4 border-t border-slate-100">
                  <details className="group">
                    <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-primary-700 transition-colors list-none flex items-center justify-between">
                      <span>Preview Template</span>
                      <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <div className="mt-3 p-4 bg-white rounded-lg border border-slate-200 text-sm text-slate-600 whitespace-pre-line max-h-64 overflow-y-auto">
                      {template.content}
                    </div>
                  </details>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 bg-amber-50 rounded-2xl p-8 border border-amber-200">
          <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
            How to Use These Templates
          </h3>
          <ol className="space-y-2 text-slate-700">
            <li className="flex items-start space-x-2">
              <span className="font-semibold">1.</span>
              <span>Download the template in your preferred format (PDF or Word)</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-semibold">2.</span>
              <span>Fill in the required information with your specific details</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-semibold">3.</span>
              <span>Review and edit the content to match your situation</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-semibold">4.</span>
              <span>Submit through the appropriate channels as indicated in the template</span>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default Templates;
